import { useState, useEffect, useCallback, useRef } from "react"
import type { FC } from "react"
import { Trans } from "react-i18next"
import { usePostsInfinite } from "../hooks/usePosts"
import { useUser } from "@features/user"
import PostView from "./post/PostView"
import PostFilter from "./post/PostFilter"
import NewPostForm from "./post/NewPostForm"

const PostsFeed: FC = () => {
  const { data: userData } = useUser()
  const managePosts = userData?.can?.managePosts ?? false
  const createPosts = userData?.can?.createPosts ?? false
  const userType = userData?.user?.type ?? 1

  const [query, setQuery] = useState("")
  const [searchQuery, setSearchQuery] = useState("")
  const [selectedTags, setSelectedTags] = useState<number[]>([])

  const filter = { tags: selectedTags, query: searchQuery, type: userType }

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    error,
    refetch,
  } = usePostsInfinite(filter)

  const loaderRef = useRef<HTMLDivElement>(null)

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const target = entries[0]
      if (target.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage()
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage]
  )

  useEffect(() => {
    const option = { root: null, rootMargin: "200px", threshold: 0 }
    const observer = new IntersectionObserver(handleObserver, option)
    if (loaderRef.current) observer.observe(loaderRef.current)
    return () => observer.disconnect()
  }, [handleObserver])

  const posts = data?.pages.flatMap((page) => page.posts) ?? []
  const totalLoaded = posts.length
  const totalMax = data?.pages[0]?.max_posts ?? 0

  const handleAddTag = (tagId: number) => {
    setSelectedTags((prev) => [...prev, tagId])
  }

  const handleRemoveTag = (tagId: number) => {
    setSelectedTags((prev) => prev.filter((t) => t !== tagId))
  }

  const handleSearch = () => {
    setSearchQuery(query)
  }

  const handleRefresh = useCallback(() => {
    refetch()
  }, [refetch])

  return (
    <div>
      {createPosts && (
        <NewPostForm type={userType} refresh={handleRefresh} />
      )}

      <PostFilter
        type={userType}
        query={query}
        selectedTags={selectedTags}
        onQueryChange={setQuery}
        onSearch={handleSearch}
        onAddTag={handleAddTag}
        onRemoveTag={handleRemoveTag}
      />

      {isLoading && (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white shadow rounded-lg p-4 animate-pulse">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-gray-200" />
                <div className="space-y-2">
                  <div className="h-3 w-24 bg-gray-200 rounded" />
                  <div className="h-2 w-16 bg-gray-200 rounded" />
                </div>
              </div>
              <div className="space-y-2">
                <div className="h-3 w-full bg-gray-200 rounded" />
                <div className="h-3 w-3/4 bg-gray-200 rounded" />
              </div>
            </div>
          ))}
        </div>
      )}

      {isError && (
        <div className="bg-red-50 text-red-500 rounded-lg p-4 text-sm">
          {error?.message || <Trans i18nKey="dashboard.posts.error" />}
        </div>
      )}

      {!isLoading && posts.length === 0 && (
        <div className="text-center py-12 text-gray-400 text-sm">
          <Trans i18nKey="dashboard.posts.empty" />
        </div>
      )}

      {posts.map((post) => (
        <PostView
          key={post.id}
          post={post}
          refresh={handleRefresh}
          managePosts={managePosts}
        />
      ))}

      <div ref={loaderRef} className="py-4 text-center">
        {isFetchingNextPage && <i className="fa fa-spinner fa-spin text-gray-400 text-lg" />}
        {!hasNextPage && posts.length > 0 && totalLoaded === totalMax && (
          <div className="text-sm text-gray-400">
            <Trans i18nKey="dashboard.posts.end" />
          </div>
        )}
      </div>
    </div>
  )
}

export default PostsFeed